import { useState, useRef } from 'react';
import { apiFetch } from '../../utils/apiFetch';

// Status per file in the queue:
// 'queued' | 'uploading' | 'extracting' | 'ready' | 'error'
function makeQueueItem(file) {
  return {
    id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
    file,
    status: 'queued',
    error: null,
    documentId: null,
    extracted: null,
  };
}

export default function UploadPolicyModal({ onClose, onAllExtracted }) {
  const [queue, setQueue] = useState([]);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  const total = queue.length;
  const finished = queue.filter(q => q.status === 'ready' || q.status === 'error').length;
  const allDone = total > 0 && finished === total;
  const successCount = queue.filter(q => q.status === 'ready').length;
  const errorCount = queue.filter(q => q.status === 'error').length;

  function handleFileChange(e) {
    const files = Array.from(e.target.files || []);
    const pdfs = files.filter(f => f.type === 'application/pdf');
    if (pdfs.length === 0) return;
    setQueue(prev => [...prev, ...pdfs.map(makeQueueItem)]);
  }

  function handleDrop(e) {
    e.preventDefault();
    if (running) return;
    const files = Array.from(e.dataTransfer.files || []);
    const pdfs = files.filter(f => f.type === 'application/pdf');
    if (pdfs.length === 0) return;
    setQueue(prev => [...prev, ...pdfs.map(makeQueueItem)]);
  }

  function removeQueued(id) {
    setQueue(prev => prev.filter(q => q.id !== id));
  }

  // Update a single item in the queue
  function updateItem(id, updates) {
    setQueue(prev => prev.map(q => q.id === id ? { ...q, ...updates } : q));
  }

  // Sleep helper
  function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

  // Try to parse a wait time from a 429 error message (Anthropic typically suggests one).
  // Falls back to a sensible default if we can't find it.
  function parseRetryDelaySeconds(errorText, defaultSec = 30) {
    if (!errorText) return defaultSec;
    // Look for patterns like "retry after 30 seconds" or "wait X seconds"
    const m = errorText.match(/(\d+)\s*seconds?/i);
    if (m) return Math.min(120, Math.max(5, parseInt(m[1])));
    return defaultSec;
  }

  // Process one file: upload then extract, with retry on 429
  async function processOne(item) {
    const maxAttempts = 3;
    try {
      updateItem(item.id, { status: 'uploading', error: null });
      const formData = new FormData();
      formData.append('file', item.file);
      const uploadRes = await apiFetch('/api/insurance/upload', { method: 'POST', body: formData });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) throw new Error(uploadData.error || 'Upload failed');

      updateItem(item.id, { status: 'extracting', documentId: uploadData.documentId });

      let lastErr = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const extractRes = await apiFetch('/api/insurance/extract', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ documentId: uploadData.documentId }),
        });
        const extractData = await extractRes.json();

        if (extractRes.ok) {
          updateItem(item.id, { status: 'ready', extracted: extractData.extracted });
          return;
        }

        const errorMsg = extractData.error || 'Extraction failed';
        lastErr = errorMsg;
        // Retry on rate limit errors (429)
        const isRateLimit = extractRes.status === 429 || errorMsg.includes('429') || errorMsg.includes('rate_limit');
        if (isRateLimit && attempt < maxAttempts) {
          const waitSec = parseRetryDelaySeconds(errorMsg, 35);
          updateItem(item.id, {
            status: 'extracting',
            error: `Rate limit hit, waiting ${waitSec}s (attempt ${attempt + 1}/${maxAttempts})...`,
          });
          await sleep(waitSec * 1000);
          continue;
        }
        // Non-retriable or out of attempts
        throw new Error(errorMsg);
      }
      throw new Error(lastErr || 'Extraction failed after retries');
    } catch (err) {
      updateItem(item.id, { status: 'error', error: err.message });
    }
  }

  async function startProcessing() {
    if (queue.length === 0) return;
    setRunning(true);

    // Process sequentially (one at a time) to stay under Anthropic's
    // 10K input tokens/min rate limit. PDFs are large (3-8K tokens each),
    // so even 2 in parallel can blow past the limit. Add a small delay
    // between requests to spread token usage across the rate window.
    const queuedItems = queue.filter(q => q.status === 'queued');
    for (const item of queuedItems) {
      await processOne(item);
      await sleep(1500);
    }

    setRunning(false);
  }

  // Retry just the items that errored (without re-uploading - we already have the documentId)
  async function retryFailed() {
    setRunning(true);
    const failed = queue.filter(q => q.status === 'error' && q.documentId);
    for (const item of failed) {
      // Reset to extracting and try again - upload already happened
      updateItem(item.id, { status: 'extracting', error: null });
      const maxAttempts = 3;
      let lastErr = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const extractRes = await apiFetch('/api/insurance/extract', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ documentId: item.documentId }),
        });
        const extractData = await extractRes.json();
        if (extractRes.ok) {
          updateItem(item.id, { status: 'ready', extracted: extractData.extracted });
          break;
        }
        lastErr = extractData.error || 'Extraction failed';
        const isRateLimit = extractRes.status === 429 || lastErr.includes('429') || lastErr.includes('rate_limit');
        if (isRateLimit && attempt < maxAttempts) {
          const waitSec = parseRetryDelaySeconds(lastErr, 35);
          updateItem(item.id, { status: 'extracting', error: `Rate limit, waiting ${waitSec}s...` });
          await sleep(waitSec * 1000);
          continue;
        }
        updateItem(item.id, { status: 'error', error: lastErr });
        break;
      }
      await sleep(1500);
    }
    setRunning(false);
  }

  function handleDone() {
    // Pass back all successfully extracted items for review
    const ready = queue.filter(q => q.status === 'ready');
    onAllExtracted?.(ready.map(q => ({
      extracted: q.extracted,
      documentId: q.documentId,
      fileName: q.file.name,
    })));
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[4px] shadow-[0_8px_30px_rgba(0,0,0,0.15)] w-[640px] max-w-full max-h-[90vh] overflow-y-auto p-5">
        <h3 className="text-[14px] font-bold text-navy mb-3">Upload Policy Documents</h3>
        <p className="text-[11px] text-[#888] mb-3">
          Drop one or more PDFs to extract policy data. We'll process them in parallel.
        </p>

        {/* Drop zone */}
        <div
          onClick={() => !running && inputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-[4px] p-8 text-center transition-colors ${
            running
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-bullogic-blue cursor-pointer'
          }`}
        >
          <div className="text-[40px] opacity-30 mb-2">📄</div>
          <p className="text-[13px] text-navy font-bold mb-1">
            {running ? 'Processing...' : 'Drop PDFs or click to browse'}
          </p>
          <p className="text-[11px] text-[#888]">
            Multiple files supported · Auto, Home, Umbrella, etc.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Queue list */}
        {queue.length > 0 && (
          <div className="mt-4 space-y-1.5 max-h-[260px] overflow-y-auto">
            {queue.map(q => (
              <QueueItem
                key={q.id}
                item={q}
                onRemove={() => removeQueued(q.id)}
                disabled={running}
              />
            ))}
          </div>
        )}

        {/* Status summary */}
        {total > 0 && (
          <div className="mt-3 text-[11px] text-[#666]">
            {!running && !allDone && `${total} file${total !== 1 ? 's' : ''} ready`}
            {running && `Processing ${finished}/${total}...`}
            {allDone && (
              <span>
                <span className="text-deep-teal font-bold">{successCount} extracted</span>
                {errorCount > 0 && <span className="text-rose font-bold ml-2">{errorCount} failed</span>}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={running}
            className={`px-4 py-2 text-[12px] font-bold rounded-[4px] ${
              running
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'text-[#888] bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Cancel
          </button>
          {!allDone && (
            <button
              onClick={startProcessing}
              disabled={queue.length === 0 || running}
              className={`px-4 py-2 text-[12px] font-bold rounded-[4px] ${
                queue.length === 0 || running
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'
              }`}
            >
              {running ? `Processing (${finished}/${total})` : `Extract ${total || ''} File${total !== 1 ? 's' : ''}`}
            </button>
          )}
          {allDone && errorCount > 0 && (
            <button
              onClick={retryFailed}
              className="px-4 py-2 text-[12px] font-bold bg-amber-500 text-white rounded-[4px] hover:bg-amber-600"
            >
              Retry Failed ({errorCount})
            </button>
          )}
          {allDone && successCount > 0 && (
            <button
              onClick={handleDone}
              className="px-4 py-2 text-[12px] font-bold bg-deep-teal text-white rounded-[4px] hover:bg-deep-teal/90"
            >
              Review & Save ({successCount})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function QueueItem({ item, onRemove, disabled }) {
  const statusColors = {
    queued: 'text-[#888]',
    uploading: 'text-bullogic-blue',
    extracting: 'text-bullogic-blue',
    ready: 'text-deep-teal',
    error: 'text-rose',
  };
  const statusLabel = {
    queued: 'Queued',
    uploading: 'Uploading...',
    extracting: 'AI extracting...',
    ready: 'Ready',
    error: 'Error',
  };
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-bone/30 rounded-[3px] text-[12px]">
      <span className="text-[16px]">📄</span>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-navy truncate">{item.file.name}</div>
        <div className={`text-[10px] ${statusColors[item.status]}`}>
          {statusLabel[item.status]}
          {item.error && <span> — {item.error}</span>}
          {item.status === 'ready' && item.extracted && (
            <span className="text-[#888]"> · {item.extracted.product_type} · {item.extracted.carrier_name || 'unknown carrier'}</span>
          )}
        </div>
      </div>
      {item.status === 'queued' && !disabled && (
        <button
          onClick={onRemove}
          className="text-[#aaa] hover:text-rose text-[14px]"
          title="Remove from queue"
        >
          ×
        </button>
      )}
    </div>
  );
}
